/**
 * @file   src\Pages\CarWashLocations.tsx
 * @brief  CarWash Locations page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { React, useState, useEffect, Container, Table, Row, Col, Badge, Button, Breadcrumb, Link, useNavigate, useOutletContext, useParams, Pagination, Modal, useLocation } from '../utils/thirdpartyComponents';
import "../styles/subscriptions.scss";
import EditIcon from '../assets/images/icons/editicon';
import DeleteIcon from '../assets/images/icons/deleteicon';
import AddIcon from '../assets/images/icons/addicon';
import NextarrowIcon from '../assets/images/icons/nextarrow';
import { pageURLs } from '../utils/pageUrls';
import { AppContextType, LocationItem, Location, CompanyDetailsItem } from '../interfaces/generalInterface';
import { LocationApi, CompanyApi, CarWashApi } from "../utils/apiUrls";
import { MessageToaster } from "../utils/toastUtil";
import axios from '../config/axiosConfig';
import GenerateLink from '../components/GenerateLink';
import GeneratePaxUniqueId from '../components/GeneratePaxUniqueId';
import CarWashStatus from '../components/CarWashStatus';
import SmallLoader from '../components/SmallLoader';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { userType } from '../utils/enums';
import { formatDate, getFromLocalStorage } from '../utils/helper';
import { getCountryCodeFromRecord, getCountryDisplayName, isUnitedStates } from '../utils/countryState';


const CarWashLocations = (props: any) => {
    const carwash_owner = (props.userType === userType['CARWASH-OWNER']) ? true : false
    const [loading, setLoading] = useState(false);
    const toaster = new MessageToaster();

    const storageData = getFromLocalStorage('CARWASH_OWNER_DATA');
    const userInfo = storageData && storageData !== null ? storageData : null;
    const isLocationManager = userInfo?.userTypeId === 4 && userInfo?.permissionType === 2;

    const param = useParams();
    const navigate = useNavigate();
    // To get path from which redirection was done
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const pageNo = query.get('fromPage') ? query.get('fromPage') : 1;
    const search = query.get('search') ? query.get('search') : ''
    const [carwashGuid, setCarwashGuid] = useState<string | null>(null);
    const [subscriptionActive, setSubscriptionActive] = useState(0);
    const [locations, setLocations] = useState<Location[]>([]);
    const [hasLocationsWithACH, setHasLocationsWithACH] = useState<boolean | null>(null);
    const [currentPage, setCurrentPage] = useState(query.get('page') ? parseInt(query.get('page') as string, 10) : 1);
    const [totalPages, setTotalPages] = useState(1);
    const [companyDetails, setCompanyDetails] = useState<CompanyDetailsItem>({
        SubscriptionPaymentDate: null,
        SubscriptionStartDate: null,
        carWashGUID: '',
        carWashURL: '',
        hasUrlPermission: 0,
        subscriptionActive: 0,
        CustomerPaymentMethodId: null,
        IsCompanyActive : 0,
        CompanyName : null,
        PaxCarwashID : undefined
    })

    // State to company details
    const [company, setCompany] = useState({
        companyName: "",
        address1: "",
        address2: "",
        city: "",
        country: "",
        state: "",
        zipCode: ""
    });

    const [startDate, setStartDate] = useState<Date | null>(new Date());

    // Function to fetch company information
    const getCompany = async () => {
        try {
            const param1 = `${param.id}`;
            const { data } = (!carwash_owner) ? await axios.get(CompanyApi.GET_COMPANY + param1) : await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_DETAILS);
            setCompanyDetails(data.data)
            setCarwashGuid(data.data.carWashGUID);
            setSubscriptionActive(data.data.subscriptionActive);
        } catch (error: any) {
            console.error('Error fetching company information:', error.message);
            throw new Error(error.response?.data?.message || error.message);
        }
    };

    // To get company details
    const getCompanyAddress = async () => {
        try {
            const param3 = `${param.id}`;
            const { data } = (!carwash_owner) ? await axios.get(CompanyApi.GET_COMPANY_ADDRESS + param3) : await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_ADDRESS);
            if (data?.error === 0) {
                setCompany({
                    ...data.data,
                    country: getCountryCodeFromRecord(data.data, { fallback: false }),
                });
            }

        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        }
    }

    // Fetch company information on component mount
    useEffect(() => {
        getCompany();
        getCompanyAddress();
    }, []);

    const getLocationList = async (pageNo = 1) => {
        try {
            setLoading(true); // Show loader
            setHasLocationsWithACH(null);
            const param2 = (carwash_owner) ? `?pageNo=${pageNo}` : `${carwashGuid}?pageNo=${pageNo}`;
            const { data } = (carwash_owner) ? await axios.get(LocationApi.CARWASH_OWNER_LOCATION_LIST + param2) : await axios.get(LocationApi.LOCATION_LIST + param2);

            if (data?.error === 0 && Array.isArray(data?.data?.list)) {
                setHasLocationsWithACH(data.data.hasLocationsWithACH == 1);
                const location_list: LocationItem[] = data.data.list;

                // Check if location_list is empty
                if (location_list.length > 0) {
                    // Transform the array to match the structure of the Location interface
                    const transformedArray: Location[] = location_list.map(item => ({
                        "locationId": item.locationId,
                        "locationName": item.locationName,
                        "email": item.email || "N/A", // Handle null values
                        "hasDefaultAddress": item.hasDefaultAddress,
                        "Address": `${item.address1}${item.address2 ? `, ${item.address2}` : ''}${item.city ? `, ${item.city}` : ''}${item.state ? `, ${item.state}` : ''}${item.zipCode ? ` ${item.zipCode}` : ''}`,
                        "NumberofEquipment": item.equipmentCount,
                        "LocationStatus": item.isActive,
                        "hardwareStatus": item.hardwareStatus,
                        "ACHStatus": item.ACHStatus,
                        
                    }));
                    setLocations(transformedArray);
                    setTotalPages(Math.ceil(data.data.totalCount / data.data.limit) || 1);
                } else {
                    console.log('Location list is empty');
                    setLocations([]);  // Set to an empty array if location_list is empty
                }
            } else {
                console.log('Location list is not available or is not an array');
                setLocations([]);  // Set to an empty array if data is not available
            }
        } catch (error: any) {
            console.error('Error fetching location list:', error.message);
            throw new Error(error.response?.data?.message || error.message);
        } finally {
            setLoading(false); // Hide loader
        }
    };

    const handlePageChange = (newPage: number) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const getVisiblePageNumbers = () => {
        const totalPageNumbersToShow = 5;
        let startPage = Math.max(1, currentPage - Math.floor(totalPageNumbersToShow / 2));
        let endPage = startPage + totalPageNumbersToShow - 1;

        if (endPage > totalPages) {
            endPage = totalPages;
            startPage = Math.max(1, endPage - totalPageNumbersToShow + 1);
        }

        const pageNumbers = [];
        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        return pageNumbers;
    };

    // Access the context
    const { updateData } = useOutletContext<AppContextType>();

    // To display heading
    useEffect(() => {
        handleUpdate();
    }, [company])

    // Update the data
    const handleUpdate = () => {
        updateData({ heading: carwash_owner ? "Manage Car Wash Locations" : company.companyName, url: `/superadmin/${pageURLs.manage_carwash}?page=${pageNo}&search=${search}`, showBackButton: carwash_owner ? false : true, showMainMenu: true, activeLink: carwash_owner ? "manage-car-wash-locations" : "manage-car-wash" });
    };

    // Navigate accordung to url
    const itemClick = async (url: string) => {
        navigate(url);
    }

    const [selected_location_for_delete, setSelected_location_for_delete] = useState(0);
    const [selected_location_name_for_delete, setSelected_location_name_for_delete] = useState('');
    const DeletebuttonClick = (delete_location_id: number, delete_location_name: string) => {
            setSelected_location_for_delete(delete_location_id);
            setSelected_location_name_for_delete(delete_location_name);
            setShowLocationDeletepoup(true);
    }

    const [showLocationDeletepoup, setShowLocationDeletepoup] = useState<boolean>(false);
    //Hide ppopup
    const hideshowLocationDeleteModal = () => {
        setShowLocationDeletepoup(false);
    }
    const Ondeletelocation = async () => {
        try {
            setLoading(true); // Show loader
            const deleteLocationRequest = {
                locationId: selected_location_for_delete,
            };
            const responseLocationDelete = (!carwash_owner) ?
            await axios.post(LocationApi.DELETE_LOCATION, deleteLocationRequest)
            :
            await axios.post(LocationApi.CARWASH_OWNER_DELETE_LOCATION, deleteLocationRequest)
            ;
            if (responseLocationDelete?.data?.error === 0) {
                toaster.toastSuccess(responseLocationDelete?.data?.message);
                // window.location.reload();
                setShowLocationDeletepoup(false);
            }
            else {
                toaster.toastError(responseLocationDelete?.data?.message);
                setShowLocationDeletepoup(false);
            }

        } catch (error: any) {
            toaster.toastError(error.response?.data?.message || error.message);
        } finally {
            setLoading(false); // Hide loader
        }
    }

    // pause location functions
    const [selected_location_for_pause, setSelected_location_for_pause] = useState(0);
    const [selected_location_status, setSelected_location_status] = useState<boolean>(false);
    const [selected_location_name_for_pause, setSelected_location_name_for_pause] = useState('');
    const [showPauseLocationpoup, setShowPauseLocationpoup] = useState<boolean>(false);
    const PausebuttonClick = (pause_location_id: number, pause_location_name: string, location_current_status: number) => {
        setSelected_location_for_pause(pause_location_id);
        setSelected_location_name_for_pause(pause_location_name);
        setShowPauseLocationpoup(true);
        setSelected_location_status(location_current_status === 0 ? true : false);
    }
    //Hide ppopup
    const hideshowPauseLocationpoupModal = () => {
        setShowPauseLocationpoup(false);
    }
    const Onpauselocation = async () => {
        try {
            setLoading(true); // Show loader
            const pauseLocationRequest = {
                locationId: selected_location_for_pause,
                status: selected_location_status
            };
            const responseLocationpause = !carwash_owner ? await axios.post(LocationApi.PAUSE_LOCATION, pauseLocationRequest)
                : await axios.post(LocationApi.CARWASH_OWNER_PAUSE_LOCATION, pauseLocationRequest);
            if (responseLocationpause?.data?.error === 0) {
                // window.location.reload();
                setShowPauseLocationpoup(false);
            }
            else {
                toaster.toastError(responseLocationpause?.data?.message);
            }

        } catch (error: any) {
            toaster.toastError(error.response?.data?.message || error.message);
        } finally {
            setLoading(false); // Hide loader
        }
    }


    // Fetch location list when carwashGuid or currentPage changes
    useEffect(() => {
        if (carwashGuid || carwash_owner) {
            getLocationList(currentPage);
        }
    }, [carwashGuid, currentPage, showLocationDeletepoup, showPauseLocationpoup]);


    useEffect(() => {
        if (companyDetails.SubscriptionPaymentDate) {
            setStartDate(new Date(companyDetails.SubscriptionPaymentDate))
        }
        else {
            if (companyDetails.SubscriptionStartDate)
                setStartDate(new Date(companyDetails.SubscriptionStartDate))

        }

    }, [companyDetails])

    const handleDateChange = (setter: React.Dispatch<React.SetStateAction<Date | null>>) => (date: Date | null) => {
        setter(date);
    };



    const [showChangeDatepoup, setShowChangeDatepoup] = useState<boolean>(false);
    const DatechangebuttonClick = () => {
        setShowChangeDatepoup(true);
    }
    //Hide ppopup
    const hideshowChangeDatepoup = () => {
        setShowChangeDatepoup(false);
    }

    // Update initial payment date
    const OnDateChange = async () => {
        try {
            setLoading(true); // Show loader
            const params = {
                "carWashId": param.id ? parseInt(param.id) : 0,
                "paymentDate": startDate
            }
            const { data } = await axios.post(CarWashApi.UPDATE_INITIAL_PAYMENT_DATE, params);
            if (data?.error === 0) {
                setShowChangeDatepoup(false);
                getCompany()
            }
            else {
                // show error message
                toaster.toastError(data?.message);
            }

        } catch (error: any) {
            throw new Error(error?.response?.data?.message || error?.message);
        } finally {
            setLoading(false); // Hide loader
        }
    }


    const addLocation = () => {
        if (carwash_owner) {
            navigate(`/${pageURLs.add_location}`)
        } else {
            navigate(`/superadmin/${pageURLs.add_location}/${param.id}`, { state: { current_carwash_id: param.id } })
        }
    }

    const onEdit = (locationId: number) => {
        if (!carwash_owner) {
            navigate(`/superadmin/${pageURLs.edit_location}/${locationId}`, { state: { page: currentPage, current_carwash_id: param.id } })
        } else {
            navigate(`/${pageURLs.edit_location}/${locationId}`)
        }
    }



    // remove the following style(after return) tag and the parent tag(<> ... </>) when working on api
    return (
        <>
            <style>
                {`
          .outline-btn {
            background-color: transparent !important;
            color: #0182F0 !important;
            border-color: #0182F0 !important;
          }
          .outline-btn:hover,
          .outline-btn:active,
          .outline-btn:focus {
            background-color: transparent !important;
            color: #0182F0 !important;
            border-color: #0182F0 !important;
          }
          .outline-btn.clicked {
            background-color: transparent !important;
            color: #0182F0 !important;
            border-color: #0182F0 !important;
          }
        `}
            </style>

            <div className='manage-carwash-container manage-subscription-container'>
                <Container>

                    {carwash_owner ? null :
                        <>
                            <Breadcrumb>
                                <Breadcrumb.Item onClick={() => { itemClick(`/superadmin/${pageURLs.manage_carwash}`) }}>Manage Car Washes</Breadcrumb.Item>
                                <Breadcrumb.Item active> {company.companyName}</Breadcrumb.Item>
                            </Breadcrumb>
                        </>
                    }
                    <div className="manage-activity-wrap d-flex justify-content-between align-items-center flex-wrap">
                        <div className="d-flex flex-column gap-3">
                            {companyDetails && <GenerateLink id={param.id} companyDetails={companyDetails} carwash_owner={carwash_owner} companyCountry={company.country} />}
                            {!carwash_owner && (
                                <GeneratePaxUniqueId carWashId={param.id} paxCarwashId={companyDetails?.PaxCarwashID} />
                            )}
                        </div>
                        {!carwash_owner ? <div className="activity-status d-flex">
                            <CarWashStatus carWashId={param.id} status={(companyDetails?.IsCompanyActive == 1) ? true : false} companyName={company.companyName} />
                            <div className="stat-group d-flex align-items-center">
                                <p>Subscription Status</p>
                                <Badge className={subscriptionActive === 1 ? 'active' : 'inactive'}>
                                    {subscriptionActive === 1 ? 'Active' : (subscriptionActive === 0) ? 'Inactive' : 'N/A'}
                                </Badge>
                            </div>

                        </div> : ''}
                    </div>



                    <div className="manage-header-block button-group-header d-flex justify-content-end">
                        {carwash_owner ? null :
                            <>
                                <Button title="Manage Users" variant="secondary" className="secondary-btn" onClick={() => navigate(`/superadmin/${pageURLs.manage_users}/${param.id}`)}>Manage Users</Button>
                                <Button title="Manage LPR Devices" variant="secondary" className="secondary-btn" onClick={() => navigate(`/superadmin/${pageURLs.lpr_device_listing}/${param.id}`)}>Manage LPR Devices</Button>
                                <Button title="Revenue Report" variant="secondary" className="secondary-btn" onClick={() => navigate(`/superadmin/${pageURLs.revenue_reports}/${param.id}`)}>Revenue Report</Button>
                                <Button title="Manage Promotions" variant="secondary" className="secondary-btn" onClick={() => navigate(`/superadmin/${pageURLs.manage_promotions}/${param.id}`)}>Manage Promotions</Button>
                                {/* <Button title="Manage Advertisements" variant="secondary" className="secondary-btn" onClick={() => navigate(`/superadmin/${pageURLs.manage_advertisements}/${param.id}`)}>Manage Advertisements</Button> */}
                                {/* <Button title="Manage Referrals" variant="secondary" className="secondary-btn" onClick={() => navigate(`/superadmin/${pageURLs.referral_program}/${param.id}`)}>Manage Referrals</Button>*/}
                                <Button title="Manage Car Wash Subscriptions" variant="secondary" className="secondary-btn" onClick={() => navigate(`/superadmin/${pageURLs.manage_subscriptions}/${param.id}`, { state: { company: company, carwashGuid: carwashGuid } })}>Manage Car Wash Subscriptions</Button>
                            </>}
                        {!isLocationManager
                        && 
                        <Button
                            type="submit"
                            title="Add Location"
                            variant="primary"
                            className="primary-btn d-flex align-items-center"
                            onClick={() => addLocation()}
                        >
                            Add Location
                            <AddIcon />
                        </Button>
                        }
                    </div >
                    {
                        carwash_owner ? null :
                            <div className="table-title-block d-flex align-items-center justify-content-between">
                                <h5 className='f-bold'>Car Wash Locations</h5>
                                <div className="payment-info d-flex align-items-center">
                                    {/** Display initial payment date if next payment date is null otherwise show next payment date */}
                                    {/* {(companyDetails && companyDetails.CustomerPaymentMethodId) ? <p className='f-semi'>{companyDetails.SubscriptionPaymentDate === null ? companyDetails.SubscriptionStartDate && `Initial Payment Date : ${formatDate(companyDetails.SubscriptionStartDate)}` : companyDetails.SubscriptionPaymentDate && `Next Payment Date : ${formatDate(companyDetails.SubscriptionPaymentDate)}`}</p> : ''} */}
                                    
                                    {(!loading && hasLocationsWithACH === false && isUnitedStates(company.country)) ? <p className='f-semi error-msg'>Payment info not added</p> : ''}

                                    {companyDetails && companyDetails.CustomerPaymentMethodId && companyDetails.SubscriptionPaymentDate === null && companyDetails.SubscriptionStartDate ? <Button
                                        title='Change'
                                        variant="primary"
                                        className="primary-btn d-flex align-items-center"
                                        onClick={DatechangebuttonClick}
                                    > 
                                        Change
                                        <NextarrowIcon />
                                    </Button> : null}
                                </div>
                            </div>
                    }
                    < div className="cw-table-wrap" >
                        <Table responsive className='m-0'>
                            <thead>
                                <tr>
                                    <th>Location Name</th>
                                    <th>Email Address</th>
                                    <th>Address</th>
                                    <th> Count of Bays</th>
                                    {carwash_owner && <th>Location Status</th>}
                                    <th>Actions</th>
                                    <th>&nbsp;</th>
                                </tr>
                            </thead>
                            <tbody>
                                {locations && locations.length > 0 ? (
                                    locations.map((item, index) => (
                                        <tr key={index}>
                                            <td>
                                                {carwash_owner ?
                                                    <Link
                                                        to={`/${pageURLs.equipment_listing}/${item["locationId"]}`}
                                                    // onClick={() => alert("Not Implemented.")}
                                                    // state={{ current_carwash_id: param.id, current_locationName: item["locationName"] }}
                                                    >
                                                        {item["locationName"]}
                                                    </Link> :
                                                    <>
                                                    <Link
                                                        to={`/superadmin/${pageURLs.equipment_listing}/${item["locationId"]}/${param.id}`}
                                                        state={{ current_carwash_id: param.id, current_locationName: item["locationName"] }}
                                                    >
                                                        {item["locationName"]}
                                                    </Link>
                                                    <br/>
                                                        {item["ACHStatus"] != 1 && isUnitedStates(company.country) && <p className="f-semi error-msg">Payment info not added</p>}
                                                    </>
                                                    
                                                    }

                                            </td>
                                            <td>{item["email"]}</td>
                                            <td>
                                                {item["hasDefaultAddress"] === 0
                                                    ? item["Address"]
                                                    : `${company.address1}${company.city ? `, ${company.city}` : ''}${company.state ? `, ${company.state}` : ''}${company.zipCode ? ` ${company.zipCode}` : ''}${getCountryDisplayName(company.country) ? `, ${getCountryDisplayName(company.country)}` : ''}`
                                                }
                                            </td>

                                            <td>{item["NumberofEquipment"]}</td>

                                            {carwash_owner && <td className='nowrap'>
                                                <div className="active-group d-flex align-items-center">
                                                    <Badge className={item.LocationStatus === 1 && item.hardwareStatus === 1 ? 'active' : 'inactive'}>{item.LocationStatus === 1 && item.hardwareStatus === 1 ? "Active" : "Inactive"}</Badge>
                                                    {/* <CWSwitch
                                                        id="statusswitch"
                                                        name="statusswitch"
                                                        checked={item.LocationStatus === 1 ? true : false}
                                                        onChange={() => alert("Not Implemented.")}
                                                    /> */}
                                                </div>
                                            </td>}

                                            <td><Button title={item.LocationStatus === 1 ? 'Pause Location' : 'Restart Location'} variant="outline-primary" className="outline-btn me-3" onClick={() => PausebuttonClick(item["locationId"], item["locationName"], item["LocationStatus"])} >{item.LocationStatus === 1 ? 'Pause Location' : 'Restart Location'}</Button>
                                            </td>


                                            <td className='nowrap'>
                                                <div className="active-group d-flex align-items-center">
                                                    <Button title="Edit" variant="light" className="light-btn action-btn" onClick={() => onEdit(item["locationId"])}><EditIcon /></Button>
                                                    <Button title='Delete' variant="light" className="light-btn action-btn" onClick={() => DeletebuttonClick(item["locationId"], item["locationName"])}><DeleteIcon /></Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td style={{ textAlign: 'center' }} colSpan={7} >No data found.</td>
                                    </tr>
                                )}

                            </tbody>
                        </Table>
                        {
                            locations && locations.length > 0 ? (
                                <div className="mt-3 d-flex justify-content-end">
                                    <Pagination>
                                        <Pagination.First
                                            disabled={currentPage === 1}
                                            onClick={() => handlePageChange(1)}
                                        />
                                        <Pagination.Prev
                                            disabled={currentPage === 1}
                                            onClick={() => handlePageChange(currentPage - 1)}
                                        />
                                        {getVisiblePageNumbers().map((pageNumber) => (
                                            <Pagination.Item
                                                key={pageNumber}
                                                active={pageNumber === currentPage}
                                                onClick={() => handlePageChange(pageNumber)}
                                            >
                                                {pageNumber}
                                            </Pagination.Item>
                                        ))}
                                        <Pagination.Next
                                            disabled={currentPage === totalPages}
                                            onClick={() => handlePageChange(currentPage + 1)}
                                        />
                                        <Pagination.Last
                                            disabled={currentPage === totalPages}
                                            onClick={() => handlePageChange(totalPages)}
                                        />
                                    </Pagination>
                                </div>
                            ) : (
                                ''
                            )
                        }
                    </div >

                </Container >
                <Modal
                    show={showLocationDeletepoup}
                    onHide={hideshowLocationDeleteModal}
                    centered
                    className='add-modal'
                >
                    <Modal.Body className='text-center'>
                        <h5 className='f-bold text-center'>Do you want to delete the location {selected_location_name_for_delete}?</h5>
                        <div className="btn-sec d-grid">
                            <Button variant="light" className="secondary-btn" onClick={hideshowLocationDeleteModal} title='Not Now'>Not Now</Button>
                            <Button variant="primary" className='primary-btn btn-main m-0' onClick={Ondeletelocation} title='Yes'>Yes</Button>
                        </div>
                    </Modal.Body>
                </Modal>
                <Modal
                    show={showChangeDatepoup}
                    onHide={hideshowChangeDatepoup}
                    centered
                    className='add-modal'
                >
                    <Modal.Body className='text-center'>
                        <h5 className='f-bold text-center'>Change Payment Date</h5>
                        <div className='cw-form-outer-wrap'>
                            <div className="cw-form-container">
                                <div className="form-inner mx-auto">
                                    <Row>
                                        <Col md={12}>
                                            <div className="form-group date-space">
                                                <p className="label">Select Date</p>
                                                <DatePicker
                                                    showIcon
                                                    selected={startDate}
                                                    onChange={handleDateChange(setStartDate)}
                                                    placeholderText='MM/DD/YYYY'
                                                />
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            </div>
                        </div>

                        <div className="btn-sec d-grid">
                            <Button variant="light" className="secondary-btn" onClick={hideshowChangeDatepoup} title='Cancel'>Cancel</Button>
                            <Button variant="primary" className='primary-btn btn-main m-0' onClick={OnDateChange} title='Submit'>Submit</Button>
                        </div>
                    </Modal.Body>
                </Modal>
                <Modal
                    show={showPauseLocationpoup}
                    onHide={hideshowPauseLocationpoupModal}
                    centered
                    className='add-modal'
                >
                    <Modal.Body className='text-center'>
                        <h5 className='f-bold text-center'>Do you want to {selected_location_status ? 'restart' : 'pause'} the location {selected_location_name_for_pause}?</h5>
                        <div className="btn-sec d-grid">
                            <Button variant="light" className="secondary-btn" onClick={hideshowPauseLocationpoupModal} title='Not Now'>Not Now</Button>
                            <Button variant="primary" className='primary-btn btn-main m-0' onClick={Onpauselocation} title='Yes'>Yes</Button>
                        </div>
                    </Modal.Body>
                </Modal>
                {loading && <SmallLoader />}
            </div >
        </>
    )
}

export default CarWashLocations;
